import * as React from 'react'
import * as classNames from 'classnames'

import { reportSearch } from '../../../common/analytics/searchBarAnalytics'

import HighlightSearchTerm from './highlightSearchTerm'
import SearchIcon from '../icons/search'
import CloseIcon from '../icons/close'
import ExpandIcon from '../icons/expand'

import * as styles from './autosuggest.module.css'

export interface ISuggestion {
  link: string
  text: string
  type: string
  categories: string[]
  category: string
}

interface IProps {
  suggestions: ISuggestion[]
  onInputChange: () => void
  placeholder: string
  onSelectOption: () => void
  inputName: string
  inputValue: string
  size: string
  focusedIndex: number
  textInputRef: HTMLInputElement
  wrapperRef: HTMLDivElement
  setMenuRef: React.LegacyRef<HTMLDivElement>
  onMenuClick: (arg0: ISuggestion, arg1: React.MouseEvent) => void
  handleMenuMouseEnter: (arg0: number) => void
  handleMenuMouseLeave: (arg0: number) => void
  isOpen: boolean
  type: string
  ariaLabel: string
  setWrapperRef: () => void
  setTextInputRef: () => void
  handleInputChange: () => void
  handleInputFocus: () => void
  handleKeyDown: () => void
  handleKeyUp: () => void
  onSearchDismiss: () => void
  handleButtonFocus: () => void
}

class AutoSuggest extends React.Component<IProps> {
  inputValue() {
    return this.props.inputValue || ''
  }

  renderWrapper() {
    const {
      suggestions,
      focusedIndex,
      textInputRef,
      wrapperRef,
      setMenuRef,
      onMenuClick,
      handleMenuMouseEnter,
      handleMenuMouseLeave
    } = this.props

    /**
     * Hack inline style :magic:
     */
    const wrapperStyle: React.CSSProperties = {
      position: 'absolute',
      top: textInputRef.clientHeight,
      width: wrapperRef.clientWidth
    }

    return (
      <div
        className={styles.autosuggestWrapper}
        data-test-selector="autosuggestWrapper"
        style={wrapperStyle}
      >
        <section ref={setMenuRef} className={styles.autosuggestList}>
          <ul data-test-selector="autosuggestList">
            {suggestions.map((suggestion, index) => {
              const autosuggestListItemClassNames = classNames({
                [styles.autosuggestListItemFocused]: focusedIndex === index
              })
              return (
                <li
                  data-test-selector="autosuggestListItem"
                  className={autosuggestListItemClassNames}
                  onMouseDown={event => onMenuClick(suggestion, event)}
                  onMouseEnter={() => handleMenuMouseEnter(index)}
                  onMouseLeave={() => handleMenuMouseLeave(index)}
                  key={index}
                >
                  <a className={styles.autosuggestLink} href={suggestion.link}>
                    <HighlightSearchTerm
                      term={this.inputValue().trim()}
                      suggestionText={suggestion.text}
                    />
                    <ExpandIcon
                      className={styles.expandIcon}
                      width="18"
                      height="18"
                    />
                  </a>
                </li>
              )
            })}
          </ul>
        </section>
      </div>
    )
  }

  render() {
    const {
      isOpen,
      suggestions,
      inputName,
      placeholder,
      type,
      ariaLabel,
      setWrapperRef,
      setTextInputRef,
      handleInputChange,
      handleInputFocus,
      handleKeyDown,
      handleKeyUp,
      onSearchDismiss,
      handleButtonFocus,
      size
    } = this.props

    const inputStyle = {
      borderRadius: isOpen && suggestions.length ? '2px 0 0 0' : '2px 0 0 2px'
    }

    const buttonStyle = {
      borderRadius: isOpen && suggestions.length ? '0 2px 0 0' : '0 2px 2px 0'
    }

    const searchWrapperStyle = classNames(styles.searchWrapper, {
      [styles.small]: size === 'small'
    })

    return (
      <div ref={setWrapperRef} className={searchWrapperStyle}>
        <input
          ref={setTextInputRef}
          className={styles.searchInput}
          style={inputStyle}
          placeholder={placeholder || ''}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          type={type || 'search'}
          aria-label={ariaLabel || 'search'}
          name={inputName || ''}
          value={this.inputValue()}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          data-test-selector="autosuggest"
        />
        {isOpen &&
          suggestions &&
          suggestions.length > 0 &&
          this.renderWrapper()}
        {this.inputValue() !== '' && (
          <button
            onClick={onSearchDismiss}
            className={styles.closeButton}
            onFocus={handleButtonFocus}
          >
            <CloseIcon width="10" height="10" />
          </button>
        )}
        <button
          className={styles.searchBtn}
          style={buttonStyle}
          defaultValue="Submit"
          onFocus={handleButtonFocus}
          onClick={() => reportSearch('Search bar')}
        >
          <span className={styles.searchBtnLabel}>Search</span>
          <SearchIcon className={styles.searchBtnIcon} width="20" height="20" />
        </button>
      </div>
    )
  }
}

export default AutoSuggest
